// Spend tiers for the marketing rewards program.
// The whole invoice lands in one tier. Eligible spend is the invoice
// subtotal after discounts and before tax. Points use the same floor
// as the database: FLOOR(eligible_cents * points_per_dollar / 100).

export type RewardTier = {
  minCents: number
  maxCents: number | null
  pointsPerDollar: number
}

export function dollarsToCents(value: number): number {
  if (!Number.isFinite(value)) return 0
  return Math.round(value * 100)
}

export function eligibleCents(subtotalCents: number, discountCents: number): number {
  return Math.max(0, Math.round(subtotalCents) - Math.round(discountCents))
}

/** Highest minimum that still contains this amount. Ranges are inclusive. */
export function matchingTier(tiers: RewardTier[], amountCents: number): RewardTier | null {
  let best: RewardTier | null = null
  for (const tier of tiers) {
    if (!(tier.pointsPerDollar > 0)) continue
    if (amountCents < tier.minCents) continue
    if (tier.maxCents != null && amountCents > tier.maxCents) continue
    if (!best || tier.minCents > best.minCents) best = tier
  }
  return best
}

export function pointsForEligible(tiers: RewardTier[], amountCents: number): number {
  const tier = matchingTier(tiers, amountCents)
  if (!tier) return 0
  const milli = Math.round(tier.pointsPerDollar * 10000)
  if (milli <= 0 || amountCents <= 0) return 0
  return Math.floor((amountCents * milli) / 100 / 10000)
}

/**
 * After a full payment, a refund scales the pre-tax total by how much of
 * the invoice (including tax) is still paid. The rate stays the one snapped
 * when the invoice was first paid.
 */
export function scaledEligibleCents(
  eligible: number,
  totalCents: number,
  amountPaidCents: number,
): number {
  if (eligible <= 0 || totalCents <= 0) return 0
  const paid = Math.min(Math.max(amountPaidCents, 0), totalCents)
  return Math.floor((eligible * paid) / totalCents)
}

export function tierProblem(tiers: RewardTier[]): string | null {
  if (tiers.length === 0) return 'Add at least one tier.'
  const sorted = [...tiers].sort((a, b) => a.minCents - b.minCents || (a.maxCents ?? Number.POSITIVE_INFINITY) - (b.maxCents ?? Number.POSITIVE_INFINITY))
  for (let i = 0; i < sorted.length; i++) {
    const tier = sorted[i]
    if (tier.minCents < 0) return 'A tier cannot start below $0.'
    if (!(tier.pointsPerDollar > 0)) return 'Each tier needs points per dollar greater than zero.'
    if (tier.maxCents != null && tier.maxCents < tier.minCents) return 'A tier’s end has to be at least its start.'
    if (tier.maxCents == null && i !== sorted.length - 1) return 'Only the highest tier can have no upper limit.'
    if (i > 0) {
      const prev = sorted[i - 1]
      if (prev.maxCents == null || tier.minCents <= prev.maxCents) {
        return 'Tiers overlap. Each invoice total can fall in only one tier.'
      }
    }
  }
  return null
}

export function tiersHaveGap(tiers: RewardTier[]): boolean {
  const sorted = [...tiers].sort((a, b) => a.minCents - b.minCents)
  for (let i = 1; i < sorted.length; i++) {
    const prev = sorted[i - 1]
    if (prev.maxCents == null) return false
    if (sorted[i].minCents > prev.maxCents + 1) return true
  }
  return false
}
